// Splitting the roster for the Commander view.
//
// ONE PERSON, ONE GROUP. The drifted used to be a SUBSET of the active and were
// also rendered under Standing by, so anyone who had gone quiet appeared twice -
// once in Active and once below. The same person, not two, which is how it was
// reported.
//
// Three groups, and every contributor lands in exactly one:
//
//   current   did work and is still doing it
//   drifted   did work, but not in the second half of the window
//   silent    no scored events at all in the window
//
// Pure, and takes `now` rather than reading the clock, so a test can pin it.

export interface GroupableContributor {
  user_id: string;
  last_seen_at: string | null;
  counters: { events: number };
}

export interface CommanderGroups<T extends GroupableContributor> {
  /** Everyone with at least one event, drifted included. Kept because other
   *  parts of the view rank the whole window. */
  active: T[];
  current: T[];
  drifted: T[];
  silent: T[];
}

export function partitionContributors<T extends GroupableContributor>(
  contributors: T[],
  windowHours: number,
  now: number = Date.now(),
): CommanderGroups<T> {
  // Half the window. A contributor whose last event is older than this is
  // treated as having gone quiet, even though they did work in the window.
  const cutoffMs = now - (windowHours * 60 * 60 * 1000) / 2;
  const isDrifted = (c: T) =>
    !!c.last_seen_at && new Date(c.last_seen_at).getTime() < cutoffMs;

  const active = contributors.filter((c) => c.counters.events > 0);
  const silent = contributors.filter((c) => c.counters.events === 0);
  const drifted = active.filter(isDrifted);
  const current = active.filter((c) => !isDrifted(c));

  return { active, current, drifted, silent };
}
